/**
 * FE impersonation guard — defense-in-depth for /v1/views/* reads.
 *
 * ## Problem
 *
 * When a Commander impersonates a rep (Rüdiger), FE sets `X-Impersonate-User`
 * and the service layer is supposed to serve the impersonated user's scope.
 * In practice, multiple paths have leaked Commander's scope through
 * impersonation (campaign_callers multi-row race, Path A deploy lag, cache
 * staleness). When that happens, the UI silently shows Commander's data to
 * the supervisor — "Keine Kampagne zugewiesen" for the rep, 0 leads for the
 * supervisor — and the bug is invisible until a user complains.
 *
 * ## Fix
 *
 * The FE refuses to hit any view slug in `IMPERSONATION_SCOPED_VIEWS`
 * without the required scope filter in the query. `caller_id`, `user_id`,
 * whatever the slug demands. This makes the scoping explicit at the
 * callsite — if the SL drops scope, the FE-side filter catches it (server
 * still receives the filter, and if RLS is wired correctly, zero leakage).
 *
 * ## Registry
 *
 * The registry below is the single source of truth for which view slugs
 * are user-scoped. Add new slugs here as they ship. Defaults to opt-in:
 * absent slugs are not guarded (use this for org-wide views like wackler-*
 * or manifest-*).
 *
 * ## Test harness
 *
 * `setImpersonationReader` swaps the session reader for unit tests. Tests
 * live at packages/core/lib/src/http/__tests__/impersonationGuard.test.ts.
 */

export interface ImpersonationState {
  email: string
}

const IMPERSONATION_STORAGE_KEY = 'scout.impersonation.v1'

/**
 * Registry of view slugs that require a scope filter when impersonation
 * is active. Key = view slug; value = set of filter keys, any one of
 * which satisfies the scope requirement (OR semantics — most views
 * accept either `caller_id` or `user_id`, for example).
 */
export const IMPERSONATION_SCOPED_VIEWS: Readonly<Record<string, ReadonlyArray<string>>> = {
  // Powers useCampaignConfig — the P0 that kicked off this guard. Must
  // carry caller_id so SL service-role path A routes correctly.
  'my-campaign': ['caller_id'],
  // calling-lead-card RETIRED from this registry on 2026-05-18 with the
  // share-don't-pre-assign cutover (FR-CALLING-CAMPAIGN-EC-REGISTRY-
  // CUTOVER v2). Queue is now a shared pool — campaign membership resolves
  // server-side from JWT / X-Impersonate-User via `campaign_callers`, and
  // FE has 0 opinion at the wire layer. Keeping it here forced the FE to
  // ship `assigned_caller_id=<him>` which returned 0 rows against the
  // NULL-assigned share pool (live P0: Rüdiger saw 0 / 446). Atomic dial-
  // target claim now flows through `GET /v1/calling/queue/next` whose own
  // session_id parameter is scope-equivalent and validated server-side.
  // Calling session state is per-user; scope by caller_id or user_id.
  'calling-session-state': ['caller_id', 'user_id'],
}

/**
 * Reads the active impersonation from sessionStorage (via
 * `scout.impersonation.v1` key, same key used by serviceLayerClient).
 * Returns null when no impersonation is active, when sessionStorage is
 * unavailable (SSR, private browsing), or when the payload is malformed.
 */
function defaultReader(): ImpersonationState | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = window.sessionStorage?.getItem(IMPERSONATION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { email?: unknown }
    if (typeof parsed.email !== 'string' || !parsed.email) return null
    return { email: parsed.email }
  } catch {
    return null
  }
}

let reader: () => ImpersonationState | null = defaultReader

/**
 * Test harness — swap the reader for unit tests. Pass `null` to restore
 * the default sessionStorage-backed reader.
 */
export function setImpersonationReader(
  fn: (() => ImpersonationState | null) | null,
): void {
  reader = fn ?? defaultReader
}

export function getActiveImpersonation(): ImpersonationState | null {
  return reader()
}

export class ImpersonationGuardError extends Error {
  constructor(
    public readonly slug: string,
    public readonly requiredFilters: ReadonlyArray<string>,
    public readonly impersonatedEmail: string,
  ) {
    super(
      `FE impersonation guard: view "${slug}" requires one of [${requiredFilters.join(', ')}] `
      + `in filters when impersonating ${impersonatedEmail}. Refusing to send unscoped request. `
      + `Add the scope filter at the hook callsite (see docs/FE-IMPERSONATION-GUARD.md).`,
    )
    this.name = 'ImpersonationGuardError'
  }
}

/**
 * Check a view request against the impersonation guard. Throws
 * ImpersonationGuardError if impersonation is active AND the slug is in
 * IMPERSONATION_SCOPED_VIEWS AND no required filter key is present in
 * `filterKeys`. No-op otherwise.
 *
 * @param slug view slug (e.g. "my-campaign")
 * @param filterKeys actual filter keys present in the request query string
 *                   (e.g. Object.keys(opts.filters ?? {}))
 */
export function assertImpersonationScope(
  slug: string,
  filterKeys: ReadonlyArray<string>,
): void {
  const required = IMPERSONATION_SCOPED_VIEWS[slug]
  if (!required) return

  const active = getActiveImpersonation()
  if (!active) return

  const hasRequiredFilter = required.some(key => filterKeys.includes(key))
  if (hasRequiredFilter) return

  throw new ImpersonationGuardError(slug, required, active.email)
}
